<template>
    <div>
        <!-- Filters -->
        <div class="datatable-filters-list-wrapper">
            <div
                v-for="(filter, filterKey) in params.filters"
                :key="filterKey"
                class="datatable-filters-list-item"
                @contextmenu="onFilterContextMenu($event, filterKey as number)"
            >
                <div class="datatable-filters-list-item-field">
                    <AdminSelect v-model="filter.field" size="sm" :options="fields"></AdminSelect>
                </div>
                <div class="datatable-filters-list-item-operator">
                    <slot :name="`filter-operator-${filter.field}`" v-bind="{ filter }">
                        <AdminSelect
                            v-model="filter.operator"
                            size="sm"
                            :options="operators"
                            :placeholder="lang('common::common.filters.comparison.placeholder')"
                        ></AdminSelect>
                    </slot>
                </div>
                <div class="datatable-filters-list-item-value">
                    <slot :name="`filter-field-${filter.field}`" v-bind="{ filter }">
                        <template v-if="getFilterType(filter.field) === 'date'">
                            <AdminDate v-model="filter.value" size="sm" class="w-full" />
                        </template>
                        <template v-else>
                            <AdminText v-model="filter.value" size="sm" />
                        </template>
                    </slot>
                </div>
                <div class="datatable-filters-list-item-button">
                    <Button type="button" color="default" size="sm" class="w-full" @click="removeFilter(filterKey)">
                        <icon icon="hero/outline/minus" class="datatable-filters-list-item-button-icon"></icon>
                    </Button>
                </div>
                <div class="datatable-filters-list-item-button">
                    <Button type="button" color="default" size="sm" class="w-full" @click="addFilter(filterKey)">
                        <icon icon="hero/outline/plus" class="datatable-filters-list-item-button-icon"></icon>
                    </Button>
                </div>
            </div>
        </div>

        <UiContextMenu
            :items="contextMenuItems"
            :x="contextMenu.x"
            :y="contextMenu.y"
            :show="contextMenu.visible"
            @close="closeContextMenu"
        />
    </div>
</template>

<script setup lang="ts">
import { toRef, computed, reactive, watch } from 'vue';
import { Column, ListingParams, Filter } from './types';
import { Way } from './enums';
import { lang } from '@/lang/composable';

interface Props {
    columns?: Column[];
    params?: ListingParams;
    hasActiveFilters?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    hasActiveFilters: false,
    columns: null,
    params: () => ({
        skip: 0,
        take: 10,
        sort: null,
        way: Way.ASC,
        search: null,
        filters: null,
    }),
});

const emit = defineEmits<{
    (event: 'filtersChanged', filters: any): void;
}>();

const columns = toRef(props, 'columns');
const params = toRef(props, 'params');
// const hasActiveFilters = toRef(props, 'hasActiveFilters');

// Track last known filters state for change detection
let lastFiltersJson = JSON.stringify(params.value.filters ?? []);

// Watch for changes to filters (both user mutations and parent updates)
watch(
    () => params.value.filters,
    (filters) => {
        const filtersJson = JSON.stringify(filters ?? []);

        // Skip if nothing changed
        if (filtersJson === lastFiltersJson) {
            return;
        }

        lastFiltersJson = filtersJson;
        emit('filtersChanged', filters);
    },
    { deep: true }
);

// Sync lastFiltersJson when parent changes filters reference (e.g., from URL)
watch(
    () => props.params?.filters,
    (newFilters) => {
        lastFiltersJson = JSON.stringify(newFilters ?? []);
    }
);

const contextMenu = reactive({
    visible: false,
    x: 0,
    y: 0,
    filterIndex: -1,
});

const fields = computed(() => {
    return columns.value
        .filter((column) => column.searchable)
        .map((column) => {
            return { id: column.slug, name: column.name };
        });
});

const getDefaultField = () => fields.value.find(() => true);
const getDefaultOperator = (): string => '=';
const getDefaultFilter = (): Filter => ({
    field: getDefaultField() ? getDefaultField().id : '',
    operator: getDefaultOperator(),
    value: null,
});

const addFilter = (index: number) => {
    const filters = params.value.filters;
    filters.splice(index + 1, 0, getDefaultFilter());
    emit('filtersChanged', filters);
};

const removeFilter = (index: number) => {
    const filters = params.value.filters;
    filters.splice(index, 1);
    emit('filtersChanged', filters);
};

const addFilterToEnd = () => {
    const filters = params.value.filters ?? [];
    filters.push(getDefaultFilter());
    emit('filtersChanged', filters);
};

const removeAllFilters = () => {
    const filters = params.value.filters;
    if (filters) {
        filters.splice(0, filters.length);
    }
    emit('filtersChanged', filters);
};

const contextMenuItems = computed(() => [
    {
        label: lang('common::common.filters.add_filter'),
        icon: 'hero/outline/plus',
        action: () => {
            addFilter(contextMenu.filterIndex);
            contextMenu.visible = false;
        },
    },
    {
        label: lang('common::common.filters.remove_filter'),
        icon: 'hero/outline/minus',
        danger: true,
        action: () => {
            removeFilter(contextMenu.filterIndex);
            contextMenu.visible = false;
        },
    },
]);

const onFilterContextMenu = (event: MouseEvent, filterIndex: number) => {
    event.preventDefault();
    contextMenu.visible = true;
    contextMenu.x = event.clientX;
    contextMenu.y = event.clientY;
    contextMenu.filterIndex = filterIndex;
};

const closeContextMenu = () => {
    contextMenu.visible = false;
};

const getFilterType = (field: string): string => {
    const column = columns.value?.find((col) => col.slug === field);
    return column?.type ?? 'string';
};

defineExpose({
    addFilterToEnd,
    removeAllFilters,
});

const operators = computed(() => {
    return [
        { id: '=', name: '=' },
        { id: '!=', name: '!=' },
        { id: '>', name: '>' },
        { id: '>=', name: '>=' },
        { id: '<', name: '<' },
        { id: '<=', name: '<=' },
        { id: 'contains', name: lang('common::common.filters.comparison.contains') },
        { id: 'notcontains', name: lang('common::common.filters.comparison.not_contains') },
        { id: 'startswith', name: lang('common::common.filters.comparison.begins_with') },
        { id: 'endswith', name: lang('common::common.filters.comparison.ends_with') },
        { id: 'in', name: lang('common::common.filters.comparison.in') },
        { id: 'notin', name: lang('common::common.filters.comparison.not_in') },
    ];
});

</script>
